import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import ListItemButton from '@mui/material/ListItemButton';

export const blue = '#0d4efe';
export const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

export const UsersToolbar = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  marginBottom: 20,
}));

export const UsersSection = styled(Box)(() => ({
  marginBottom: 28,
}));

export const UsersSectionHeading = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 10,
}));

export const UsersSectionTitle = styled('h3')(() => ({
  margin: 0,
  fontFamily: "'Exo 2', sans-serif",
  fontWeight: 700,
  fontSize: '1.05rem',
  color: ink,
}));

export const UsersSectionCount = styled('span')(() => ({
  color: muted,
  fontWeight: 600,
  fontSize: '0.85rem',
}));

export const UsersListWrap = styled(Box)(() => ({
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.08)',
  background: '#fff',
  overflow: 'hidden',
  '& .MuiListItemButton-root:last-of-type': {
    borderBottom: 'none',
  },
}));

export const UsersRowButton = styled(ListItemButton)(() => ({
  padding: '14px 16px',
  alignItems: 'flex-start',
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'background 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(13, 78, 254, 0.06)',
  },
  '&:focus-visible': {
    outline: `2px solid ${blue}`,
    outlineOffset: -2,
  },
  '&.Mui-disabled': {
    opacity: 1,
    cursor: 'default',
    backgroundColor: '#f8fafc',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
}));

export const UsersField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    background: '#fff',
  },
}));

export const StyledUsersDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    width: '100%',
  },
}));

export const UsersDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  margin: 0,
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
  fontWeight: 700,
  fontSize: '1.125rem',
  color: ink,
  '& .users-dialog-icon-wrap': {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.18)',
  },
}));

export const UsersDialogCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const UsersDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  background: '#f7fafc',
}));

export const UsersDialogActions = styled(DialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2, 3, 2.5),
  gap: theme.spacing(1.25),
  background:
    'linear-gradient(180deg, rgba(247, 250, 252, 0.95) 0%, rgba(240, 244, 248, 0.98) 100%)',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const UsersPrimaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 10,
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: 'linear-gradient(90deg, #01d4a0 0%, #0b45e0 100%)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.7)',
  },
}));

export const UsersSecondaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.35)',
  '&:hover': {
    borderColor: blue,
    background: 'rgba(13, 78, 254, 0.04)',
  },
}));

export const UsersPanel = styled(Box)(() => ({
  padding: 16,
  borderRadius: 12,
  background: '#fff',
  border: '1px solid rgba(0, 0, 0, 0.06)',
}));
